import { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogHeader,
  DialogContent,
  FormSelect,
  FormTextarea,
  Label,
  QueryStateBoundary,
} from "@/components/ui";
import {
  STATUS_LABELS,
  TIER_BADGE_VARIANT,
  TIER_LABELS,
  VENDOR_LIST_STATUS_OPTIONS,
} from "@/api/types/outreach";
import {
  useLogTouch,
  useOutreachDetail,
  usePatchOutreach,
  useVerifyOutreach,
} from "@/api/hooks/useOutreach";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { formatDateTime } from "./format";

interface OutreachDetailDialogProps {
  outreachId: string | null;
  onClose: () => void;
}

export function OutreachDetailDialog({ outreachId, onClose }: OutreachDetailDialogProps) {
  const open = !!outreachId;
  const { data: outreach, isLoading, error, refetch } = useOutreachDetail(outreachId ?? undefined);
  const verifyOutreach = useVerifyOutreach();
  const logTouch = useLogTouch();
  const patchOutreach = usePatchOutreach();

  const [nextTouchAt, setNextTouchAt] = useState("");
  const [vendorListStatus, setVendorListStatus] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (outreach) {
      setNextTouchAt(outreach.next_touch_at ?? "");
      setVendorListStatus(outreach.vendor_list_status ?? "");
      setNotes(outreach.notes ?? "");
    }
  }, [outreach]);

  async function handleVerify() {
    if (!outreach) return;
    try {
      await verifyOutreach.mutateAsync(outreach.id);
      toastSuccess("Marked verified");
    } catch (err) {
      toastError("Couldn't verify", getErrorMessage(err));
    }
  }

  async function handleLogTouch() {
    if (!outreach) return;
    try {
      await logTouch.mutateAsync(outreach.id);
      toastSuccess("Touch logged");
    } catch (err) {
      toastError("Couldn't log touch", getErrorMessage(err));
    }
  }

  async function saveNextTouch() {
    if (!outreach || nextTouchAt === (outreach.next_touch_at ?? "")) return;
    try {
      await patchOutreach.mutateAsync({ id: outreach.id, next_touch_at: nextTouchAt || null });
    } catch (err) {
      toastError("Couldn't save next-touch date", getErrorMessage(err));
    }
  }

  async function saveVendorListStatus(value: string) {
    if (!outreach) return;
    setVendorListStatus(value);
    try {
      await patchOutreach.mutateAsync({ id: outreach.id, vendor_list_status: value || null });
    } catch (err) {
      toastError("Couldn't save vendor-list status", getErrorMessage(err));
    }
  }

  async function saveNotes() {
    if (!outreach || notes === (outreach.notes ?? "")) return;
    try {
      await patchOutreach.mutateAsync({ id: outreach.id, notes: notes || null });
    } catch (err) {
      toastError("Couldn't save notes", getErrorMessage(err));
    }
  }

  return (
    <Dialog open={open} onClose={onClose} ariaLabel="Outreach detail">
      <DialogContent size="lg">
        <DialogHeader onClose={onClose}>{outreach?.contact_name ?? "Outreach"}</DialogHeader>
        <DialogBody className="space-y-6">
          <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
            {outreach && (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={TIER_BADGE_VARIANT[outreach.tier]}>
                    {TIER_LABELS[outreach.tier]}
                  </Badge>
                  <Badge variant="secondary">{STATUS_LABELS[outreach.status]}</Badge>
                  {!outreach.verified && (
                    <Badge variant="warning" data-testid="detail-unverified-chip">
                      ⚠ verify
                    </Badge>
                  )}
                </div>

                {!outreach.verified && (
                  <div
                    className="rounded-md border border-warning/30 bg-warning-light px-3 py-2.5"
                    data-testid="unverified-banner"
                  >
                    <p className="text-sm text-warning">
                      This target came from an LLM-generated playbook and has not been
                      confirmed. Verify it with Chris before making first contact.
                    </p>
                    <Button
                      size="sm"
                      className="mt-2"
                      onClick={() => void handleVerify()}
                      disabled={verifyOutreach.isPending}
                    >
                      {verifyOutreach.isPending ? "Verifying..." : "Verify"}
                    </Button>
                  </div>
                )}

                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm text-text-secondary">
                    {outreach.last_touch_at
                      ? `Last touched ${formatDateTime(outreach.last_touch_at)}`
                      : "Never touched"}
                  </p>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => void handleLogTouch()}
                    disabled={logTouch.isPending}
                  >
                    {logTouch.isPending ? "Logging..." : "Log touch"}
                  </Button>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="outreach-next-touch">Next touch date</Label>
                    <input
                      id="outreach-next-touch"
                      type="date"
                      value={nextTouchAt}
                      onChange={(e) => setNextTouchAt(e.target.value)}
                      onBlur={() => void saveNextTouch()}
                      className="flex h-10 w-full rounded-md border border-border bg-bg-card px-3 text-sm"
                    />
                  </div>

                  <FormSelect
                    label="Vendor-list status"
                    value={vendorListStatus}
                    onChange={(e) => void saveVendorListStatus(e.target.value)}
                  >
                    <option value="">Not applicable</option>
                    {VENDOR_LIST_STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s[0].toUpperCase() + s.slice(1)}
                      </option>
                    ))}
                  </FormSelect>
                </div>

                <FormTextarea
                  label="Notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  onBlur={() => void saveNotes()}
                />
              </>
            )}
          </QueryStateBoundary>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
